import { useEffect, useState } from 'react';
import { Check, ChevronRight, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { connect } from 'react-redux';
import { PortalSpinner } from 'components/PortalSpinner';
import { getProfileCompletion } from 'pages/Profile/helper';
import { ProfileActions } from 'store/actions';
import { getUpdateCards, SECTION_KEY, PRIMARY_BTN, PENDING_BADGE } from './helper';
import Header from './Header';
import SectionStep from './SectionStep';

const enhancer = connect(null, { ...ProfileActions });

const Success = ({ showProfile, profile: loadedProfile = null, visitDate, startTime, providerName = '', onDone }) => {
  const [profile, setProfile] = useState(loadedProfile);
  const [stepId, setStepId] = useState(null);

  const attributes = profile?.attributes;
  const completion = getProfileCompletion(attributes);
  const sectionData = {
    medical: attributes?.medical_history?.data?.attributes,
    insurance: attributes?.insurance_intake?.data?.attributes,
  };

  useEffect(() => {
    async function fetchProfile() {
      try {
        const { data } = await showProfile();
        setProfile(data);
      } catch (error: any) {
        toast.error(error.errors || 'Failed to load profile. Please try again later.');
      }
    }

    if (!loadedProfile) fetchProfile();
  }, []);

  function handleSaved(data) {
    setProfile((p: any) => ({ ...p, attributes: { ...p.attributes, [SECTION_KEY[stepId]]: { data } } }));
  }

  if (stepId)
    return (
      <SectionStep
        stepId={stepId}
        data={sectionData[stepId]}
        completed={completion[stepId]}
        practice={attributes?.practice?.data?.attributes}
        onSaved={handleSaved}
        onClose={() => setStepId(null)}
      />
    );

  return (
    <div className="overflow-y-auto px-6 pb-6 pt-8 sm:px-7">
      <Header
        visitDate={visitDate}
        startTime={startTime}
        providerName={providerName}
      />

      <p className="mt-6 text-center text-sm text-secondary-400">
        Or take a quick look at what we have on file
      </p>

      {!profile ? (
        <PortalSpinner padding="py-10" />
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {getUpdateCards(sectionData).map(({ id, label, icon: Icon, iconClass, headline, logo }) => (
              <button
                key={id}
                type="button"
                onClick={() => setStepId(id)}
                className="flex items-center gap-3 rounded-xl border border-gray-200 p-3.5 text-left transition-colors hover:bg-gray-50"
              >
                {logo ? (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[11px] bg-white shadow-md">
                    <img src={logo} alt="" className="h-full w-full object-cover" />
                  </span>
                ) : (
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
                    <Icon size={18} />
                  </span>
                )}

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-secondary-400">{label}</span>
                    {!completion[id] && (
                      <span className={PENDING_BADGE}>
                        <Clock size={10} strokeWidth={2.5} />
                        Pending
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-sm font-bold leading-snug text-secondary-900 line-clamp-2">{headline}</span>
                </span>

                <ChevronRight size={17} className="shrink-0 text-secondary-300" />
              </button>
            ))}
        </div>
      )}

      <button type="button" onClick={onDone} className={`${PRIMARY_BTN} mt-5 w-full`}>
        <Check size={16} strokeWidth={2.8} />
        Looks good, all done
      </button>
    </div>
  );
};

export default enhancer(Success);
